import {
  ProviderSubscriptionStatus,
  RealtimeConnectionState
} from "./market-data-contracts.js";
import { SubscriptionRegistry } from "./subscription-registry.js";

export class RealtimeConnectionManager {
  constructor({
    grantClient,
    adapterFactory,
    connectionLease = null,
    reconcile = async () => {},
    batchDelayMs = 40,
    retryBaseMs = 2_000,
    maximumReconnectAttempts = 5,
    random = () => Math.random(),
    now = () => Date.now(),
    setTimer = (callback, delay) => setTimeout(callback, delay),
    clearTimer = timer => clearTimeout(timer)
  }) {
    this.grantClient = grantClient;
    this.adapterFactory = adapterFactory;
    this.connectionLease = connectionLease;
    this.connectionLeaseHeld = false;
    this.reconcile = reconcile;
    this.batchDelayMs = batchDelayMs;
    this.retryBaseMs = retryBaseMs;
    this.maximumReconnectAttempts = maximumReconnectAttempts;
    this.random = random;
    this.now = now;
    this.setTimer = setTimer;
    this.clearTimer = clearTimer;
    this.state = RealtimeConnectionState.DISCONNECTED;
    this.stateReason = null;
    this.registry = new SubscriptionRegistry(() => this.desiredChanged());
    this.active = new Set();
    this.listeners = new Set();
    this.adapter = null;
    this.adapterSource = null;
    this.grant = null;
    this.sidForRevocation = null;
    this.authorizeController = null;
    this.connectPromise = null;
    this.flushTimer = null;
    this.flushPromise = null;
    this.flushRequested = false;
    this.retryTimer = null;
    this.authorizationTimer = null;
    this.authorizationController = null;
    this.providerReconcilePromise = null;
    this.disposingAdapter = false;
    this.reconnectAttempts = 0;
    this.closed = false;
    this.messageCursors = new Map();
    this.eventIdentities = new Set();
    this.metrics = {
      messages: 0,
      duplicates: 0,
      outOfOrder: 0,
      unknown: 0,
      reconnects: 0
    };
  }

  on(listener) {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  subscribe(request, listener) {
    if (this.closed) throw new Error("The real-time connection manager is closed.");
    const registryHandle = this.registry.acquire(request, listener);
    this.emit(this.snapshot());
    void this.ensureConnected(false);
    return Object.freeze({
      key: registryHandle.key,
      release: () => {
        const released = registryHandle.release();
        if (released) this.emit(this.snapshot());
        return released;
      }
    });
  }

  snapshot() {
    return Object.freeze({
      kind: "connection-state",
      state: this.state,
      reason: this.stateReason,
      desiredSubscriptions: this.registry.snapshot(),
      activeSubscriptions: [...this.active],
      metrics: Object.freeze({ ...this.metrics })
    });
  }

  desiredChanged() {
    this.flushRequested = true;
    this.scheduleFlush();
    if (this.registry.desiredEntries().length > 0) void this.ensureConnected(false);
  }

  async ensureConnected(reconnecting) {
    if (this.closed || this.adapter || this.connectPromise ||
        this.registry.desiredEntries().length === 0) return this.connectPromise;
    const operation = this.openConnection(reconnecting).finally(() => {
      if (this.connectPromise === operation) this.connectPromise = null;
    });
    this.connectPromise = operation;
    return operation;
  }

  async openConnection(reconnecting) {
    let openingAdapter = null;
    let source = null;
    try {
      if (!this.connectionLeaseHeld && this.connectionLease?.acquire) {
        this.connectionLeaseHeld = await this.connectionLease.acquire();
        if (!this.connectionLeaseHeld) {
          const activeTab = new Error("Live market data is active in another browser tab.");
          activeTab.reason = "live-session-active-in-another-tab";
          activeTab.terminal = true;
          throw activeTab;
        }
      }
      if (!this.grantValid()) {
        this.setState(reconnecting
          ? RealtimeConnectionState.RECONNECTING
          : RealtimeConnectionState.AUTHORIZING);
        this.authorizeController?.abort();
        const controller = new AbortController();
        this.authorizeController = controller;
        this.grant = await this.grantClient.create(controller.signal);
        this.sidForRevocation = this.grant.sid;
        if (this.authorizeController === controller) this.authorizeController = null;
      }
      if (!reconnecting) this.setState(RealtimeConnectionState.CONNECTING);
      const grant = this.grant;
      source = {};
      this.adapterSource = source;
      // SDK close/error callbacks can arrive after disposal. Only the current
      // connection attempt may deliver events, including while connect is pending.
      const isCurrentSource = () => !this.closed && this.adapterSource === source;
      const adapter = this.adapterFactory({
        onMessage: message => { if (isCurrentSource()) this.handleMessage(message); },
        onEvent: event => { if (isCurrentSource()) this.handleProviderEvent(event); },
        onDiagnostic: diagnostic => { if (isCurrentSource()) this.handleDiagnostic(diagnostic); }
      });
      openingAdapter = adapter;
      await adapter.connect(grant);
      if (!isCurrentSource()) {
        adapter.close();
        return;
      }
      this.grant = null;
      this.adapter = adapter;
      openingAdapter = null;
      this.active.clear();
      await this.flushNow();
      if (!this.adapter || this.registry.desiredEntries().length === 0) return;
      if (reconnecting) {
        await this.reconcile({
          reason: "reconnect",
          subscriptions: this.registry.snapshot()
        });
        this.metrics.reconnects += 1;
      }
      this.reconnectAttempts = 0;
      this.setState(RealtimeConnectionState.CONNECTED);
      this.scheduleAuthorizationCheck();
      adapter.getSessionStats?.();
    } catch (error) {
      const superseded = source !== null && this.adapterSource !== source;
      if (this.adapterSource === source) this.adapterSource = null;
      openingAdapter?.close();
      this.authorizeController = null;
      if (this.closed || superseded || error?.name === "AbortError") return;
      if (error?.status === 401 || error?.status === 403) {
        this.releaseProviderSession();
        this.grant = null;
        this.releaseConnectionLease();
        this.setState(RealtimeConnectionState.UNAUTHORIZED, error.reason);
        return;
      }
      if (error?.terminal) {
        this.releaseProviderSession();
        this.grant = null;
        this.releaseConnectionLease();
        this.setState(RealtimeConnectionState.DEGRADED, error.reason);
        return;
      }
      this.scheduleReconnect(error?.reason || "connection-failed", {
        preserveGrant: this.grantValid()
      });
    }
  }

  scheduleFlush() {
    if (this.closed || this.flushTimer !== null) return;
    this.flushTimer = this.setTimer(() => {
      this.flushTimer = null;
      void this.flushNow();
    }, this.batchDelayMs);
  }

  async flushNow() {
    if (!this.adapter) return null;
    if (this.flushPromise) {
      this.flushRequested = true;
      return this.flushPromise;
    }
    this.flushRequested = false;
    const operation = this.applyDesiredSubscriptions().finally(() => {
      if (this.flushPromise === operation) {
        this.flushPromise = null;
        if (this.flushRequested) this.scheduleFlush();
      }
    });
    this.flushPromise = operation;
    return operation;
  }

  async applyDesiredSubscriptions() {
    const desiredEntries = this.registry.desiredEntries();
    const desiredKeys = new Set(desiredEntries.map(entry => entry.key));
    const removals = [...this.active]
      .filter(key => !desiredKeys.has(key))
      .map(key => this.registry.get(key))
      .filter(Boolean);
    if (removals.length > 0) {
      await this.adapter.unsubscribe(removals);
      for (const entry of removals) this.active.delete(entry.key);
    }
    this.registry.pruneUnused(this.active);
    const additions = desiredEntries.filter(entry => !this.active.has(entry.key));
    if (additions.length === 0) {
      if (desiredEntries.length === 0) this.disconnectIdleAdapter();
      return;
    }
    for (const entry of additions) {
      this.registry.markResult(entry.key, ProviderSubscriptionStatus.PENDING);
    }
    const results = await this.adapter.subscribe(additions);
    for (const result of results) {
      if (result.status === ProviderSubscriptionStatus.SUBSCRIBED) {
        this.active.add(result.key);
      } else {
        this.active.delete(result.key);
      }
      this.registry.markResult(result.key, result.status, result.metadata);
    }
    this.emit(this.snapshot());
  }

  disconnectIdleAdapter() {
    this.clearAuthorizationCheck();
    const adapter = this.adapter;
    this.adapter = null;
    this.adapterSource = null;
    this.active.clear();
    this.releaseProviderSession();
    this.releaseConnectionLease();
    this.disposingAdapter = true;
    try {
      adapter?.close();
    } finally {
      this.disposingAdapter = false;
    }
    if (!this.closed) this.setState(RealtimeConnectionState.DISCONNECTED, "idle");
  }

  handleMessage(message) {
    if (!message || typeof message !== "object") {
      this.handleDiagnostic({ kind: "unknown-message" });
      return;
    }
    const identity = message.providerEventId ||
      (message.providerSequence
        ? `${message.providerSymbol}|${message.type}|${message.providerSequence}`
        : null);
    if (identity && this.eventIdentities.has(identity)) {
      this.metrics.duplicates += 1;
      return;
    }
    if (identity) {
      this.eventIdentities.add(identity);
      if (this.eventIdentities.size > 10_000) {
        this.eventIdentities.delete(this.eventIdentities.values().next().value);
      }
    }
    const cursorKey = `${message.providerSymbol}|${message.type}`;
    const cursor = this.messageCursors.get(cursorKey);
    if (isOutOfOrder(cursor, message)) {
      this.metrics.outOfOrder += 1;
      // Snapshot timestamps are the last trade time, not a transport sequence.
      // A delayed snapshot must not trigger full history reloads for every tick.
      if (!(["PRICEDATA", "QUOTE"].includes(message.type) && numericSequence(message.providerSequence) === null)) {
        this.emit(Object.freeze({ kind: "gap-suspected", message }));
      }
      return;
    }
    this.messageCursors.set(cursorKey, {
      sequence: message.providerSequence,
      timestamp: message.eventTimestampMs
    });
    this.metrics.messages += 1;
    this.registry.dispatch(message);
  }

  handleProviderEvent(event) {
    this.emit(Object.freeze({ kind: "provider-event", event }));
    if (this.disposingAdapter) return;
    if (event.kind === "close" || event.kind === "error" || event.kind === "slow") {
      this.scheduleReconnect(event.detail?.code === 452 ? "connection-limit" : event.kind);
    } else if (["reconnectSuccess", "reconnectMessage", "missedDataSent"].includes(event.kind)) {
      this.reconcileProviderRecovery(event.kind);
    }
  }

  reconcileProviderRecovery(reason) {
    if (this.closed || !this.adapter || this.providerReconcilePromise) return;
    this.setState(RealtimeConnectionState.RECONNECTING, reason);
    const operation = Promise.resolve(this.reconcile({
      reason,
      subscriptions: this.registry.snapshot()
    })).then(() => {
      if (!this.closed && this.adapter) this.setState(RealtimeConnectionState.CONNECTED);
    }).catch(() => this.scheduleReconnect("reconciliation-failed"))
      .finally(() => {
        if (this.providerReconcilePromise === operation) this.providerReconcilePromise = null;
      });
    this.providerReconcilePromise = operation;
  }

  handleDiagnostic(diagnostic) {
    if (diagnostic?.kind === "unknown-message") this.metrics.unknown += 1;
    this.emit(Object.freeze({ kind: "diagnostic", diagnostic }));
  }

  scheduleReconnect(reason, { preserveGrant = false } = {}) {
    if (this.closed || this.retryTimer !== null ||
        this.registry.desiredEntries().length === 0) return;
    this.clearAuthorizationCheck();
    const adapter = this.adapter;
    this.adapter = null;
    this.adapterSource = null;
    this.disposingAdapter = true;
    try {
      adapter?.close();
    } finally {
      this.disposingAdapter = false;
    }
    this.active.clear();
    if (!preserveGrant) {
      this.releaseProviderSession();
      this.grant = null;
    }
    this.reconnectAttempts += 1;
    if (this.reconnectAttempts > this.maximumReconnectAttempts) {
      this.releaseProviderSession();
      this.grant = null;
      this.releaseConnectionLease();
      this.setState(RealtimeConnectionState.DEGRADED, reason);
      return;
    }
    this.setState(RealtimeConnectionState.RECONNECTING, reason);
    const exponential = this.retryBaseMs * 2 ** (this.reconnectAttempts - 1);
    const delay = Math.min(15_000, exponential) * (0.75 + this.random() * 0.5);
    this.retryTimer = this.setTimer(() => {
      this.retryTimer = null;
      void this.ensureConnected(true);
    }, delay);
  }

  scheduleAuthorizationCheck(recheckAfterUtc = null) {
    if (this.closed || !this.adapter || typeof this.grantClient.check !== "function") return;
    this.clearAuthorizationCheck();
    const requestedDelay = recheckAfterUtc
      ? Date.parse(recheckAfterUtc) - this.now()
      : 60_000;
    const delay = Math.min(300_000, Math.max(15_000,
      Number.isFinite(requestedDelay) ? requestedDelay : 60_000));
    this.authorizationTimer = this.setTimer(() => {
      this.authorizationTimer = null;
      void this.checkAuthorization();
    }, delay);
  }

  async checkAuthorization() {
    if (this.closed || !this.adapter || typeof this.grantClient.check !== "function") return;
    const controller = new AbortController();
    this.authorizationController = controller;
    try {
      const result = await this.grantClient.check(controller.signal);
      if (this.closed || this.authorizationController !== controller || !this.adapter) return;
      this.authorizationController = null;
      this.scheduleAuthorizationCheck(result.recheckAfterUtc);
    } catch (error) {
      if (this.authorizationController === controller) this.authorizationController = null;
      if (this.closed || error?.name === "AbortError") return;
      if (error?.status === 401 || error?.status === 403) {
        const adapter = this.adapter;
        this.adapter = null;
        this.adapterSource = null;
        this.disposingAdapter = true;
        try {
          adapter?.close();
        } finally {
          this.disposingAdapter = false;
        }
        this.active.clear();
        this.releaseProviderSession();
        this.releaseConnectionLease();
        this.grant = null;
        this.setState(RealtimeConnectionState.UNAUTHORIZED, error.reason);
        return;
      }
      this.scheduleReconnect("authorization-check-failed");
    }
  }

  clearAuthorizationCheck() {
    if (this.authorizationTimer !== null) this.clearTimer(this.authorizationTimer);
    this.authorizationTimer = null;
    this.authorizationController?.abort();
    this.authorizationController = null;
  }

  grantValid() {
    return Boolean(this.grant &&
      Date.parse(this.grant.expiresAtUtc) - this.now() > 15_000);
  }

  releaseProviderSession() {
    const sid = this.sidForRevocation;
    this.sidForRevocation = null;
    if (!sid || typeof this.grantClient.revoke !== "function") return;
    void Promise.resolve(this.grantClient.revoke(sid)).catch(() => {
      this.handleDiagnostic({ kind: "provider-session-release-failed" });
    });
  }

  releaseConnectionLease() {
    if (!this.connectionLeaseHeld) return;
    this.connectionLeaseHeld = false;
    this.connectionLease?.release?.();
  }

  setState(state, reason = null) {
    if (this.state === state && this.stateReason === reason) return;
    this.state = state;
    this.stateReason = reason;
    this.emit(this.snapshot());
  }

  emit(event) {
    for (const listener of this.listeners) {
      try {
        listener(event);
      } catch {
        // Diagnostics and UI listeners cannot disrupt the shared stream.
      }
    }
  }

  close(reason = "workspace-disposed") {
    if (this.closed) return;
    this.closed = true;
    if (this.flushTimer !== null) this.clearTimer(this.flushTimer);
    if (this.retryTimer !== null) this.clearTimer(this.retryTimer);
    this.clearAuthorizationCheck();
    this.authorizeController?.abort();
    this.adapterSource = null;
    this.adapter?.close();
    this.adapter = null;
    this.releaseProviderSession();
    this.releaseConnectionLease();
    this.grant = null;
    this.active.clear();
    this.registry.clear();
    this.setState(RealtimeConnectionState.CLOSED, reason);
    this.listeners.clear();
  }
}

function isOutOfOrder(cursor, message) {
  if (!cursor) return false;
  const previousSequence = numericSequence(cursor.sequence);
  const sequence = numericSequence(message.providerSequence);
  if (previousSequence !== null && sequence !== null) return sequence <= previousSequence;
  return Number.isFinite(cursor.timestamp) && Number.isFinite(message.eventTimestampMs) &&
    message.eventTimestampMs < cursor.timestamp;
}

function numericSequence(value) {
  if (value === null || value === undefined || !/^\d+$/.test(String(value))) return null;
  try {
    return BigInt(value);
  } catch {
    return null;
  }
}
